import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Skill } from '@atlassian/rovo-platform-types/skills';

import { buildLegacySkillsManifest, buildSkillsManifest } from '../../utils/build-skills-manifest';
import { fetchSkills } from './fetch-skills';
import { filterSkills } from './filter-skills';
import type { RovoSkillsInsertMenuConfig } from './types';

/**
 * Creates an ExtensionManifest that surfaces Rovo skills as quick insert items
 * in the editor's slash menu.
 *
 * This fetches skills, filters them, then delegates to `buildSkillsManifest`
 * to create the manifest. Products register this manifest in their extension
 * provider, and skills automatically appear in the insert menu.
 *
 * When `onSkillSelected` is provided, it is called when a user selects a skill.
 * Products handle the editor integration themselves (e.g. opening the Rovo
 * inline chat prompt with the skill chip pre-filled).
 *
 * Skills are fetched once and cached for the page session (see `fetchSkills`).
 *
 * @param cloudId - The Atlassian cloud ID for the site
 * @param config - Configuration controlling which skills to show
 *
 * @example
 * ```typescript
 * // In a product's extension provider:
 * const manifest = await createRovoSkillsExtensionManifest(cloudId, {
 *   allowedSkills: ['research-insights', 'write-prd'],
 *   onSkillSelected: (slashCommand, skill) => {
 *     // Product handles opening the inline prompt
 *   },
 *   onAnalyticsEvent: (payload) => {
 *     // Forward to the editor's analytics API
 *     api?.analytics?.actions?.fireAnalyticsEvent(payload as any);
 *   },
 * });
 * modernManifests.push(manifest);
 * ```
 */
export async function createRovoSkillsExtensionManifest(
	cloudId: string,
	config: Omit<RovoSkillsInsertMenuConfig, 'enabled'>,
): Promise<ExtensionManifest<Skill>> {
	const allSkills = await fetchSkills(cloudId);
	const filteredSkills = filterSkills(allSkills, config);

	const buildManifest = isExperimentEnabled('platform_editor_slash_command')
		? buildSkillsManifest
		: buildLegacySkillsManifest;

	return buildManifest(filteredSkills, {
		isHidden: config.isHidden,
		// Skills should appear below native editor elements in the slash menu.
		// A high priority value (lower importance) ensures native items rank
		// higher when search scores are equal.
		priority: config.priority ?? 9000,
		onAction: (skill) => {
			config.onAnalyticsEvent?.({
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.TYPEAHEAD,
				actionSubjectId: ACTION_SUBJECT_ID.TYPEAHEAD_QUICK_INSERT,
				attributes: {
					skillSlug: skill.slug,
					skillName: skill.name,
					isAiSkill: true,
				},
				eventType: EVENT_TYPE.TRACK,
			});

			return config.onSkillSelected?.(`/${skill.slug}`, skill);
		},
	}) as ExtensionManifest<Skill>;
}
